import React from 'react';
import { useTranslation, resolveResourceLang } from './i18n.js';

const SLIDER_MOBILE_QUERY = '(max-width: 768px)';

// Slides de la langue demandée (fr, en, de, it, es — voir resources/slider.json), avec repli sur
// l'anglais si cette langue n'a pas de slider. Partagé avec le rendu serveur (dataLoaders.mjs).
export function pickSlides(sliderData, lang) {
  const own = sliderData && sliderData[lang];
  if (Array.isArray(own) && own.length > 0) {
    return own;
  }
  const fallback = sliderData && sliderData.en;
  return Array.isArray(fallback) ? fallback : [];
}

export function Slider({ initialData }) {
  const { lang, t } = useTranslation();
  const [slides, setSlides] = React.useState(initialData ? initialData.slides : []);
  const [defaultDelay, setDefaultDelay] = React.useState(initialData ? initialData.delay : 6000);
  const [defaultSubtitleColor, setDefaultSubtitleColor] = React.useState(
    initialData ? initialData.colorSubtitle : 'rgba(255,255,255,0.9)'
  );
  const [current, setCurrent] = React.useState(0);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(!initialData);
  const [ratios, setRatios] = React.useState({});
  // `false` des deux côtés au premier rendu (le serveur ne connaît pas la largeur de l'écran) :
  // lire le vrai `matchMedia` dès le rendu client casserait l'hydratation sur mobile (le `src`
  // de l'image serait différent de celui du HTML serveur) — on le corrige dans l'effet ci-dessous.
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (initialData) {
      // Les données du premier rendu correspondent déjà à `lang` (fournies par le serveur) ;
      // on ne refetch que si la langue change ensuite côté client.
      return;
    }

    const resourceLang = resolveResourceLang(lang);

    setLoading(true);
    setError('');
    setCurrent(0);

    fetch('/resources/slider.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Impossible de charger les données du slider.');
        }
        return response.json();
      })
      .then((sliderData) => {
        setSlides(pickSlides(sliderData, resourceLang));
        setDefaultDelay(sliderData.delay ?? 6000);
        setDefaultSubtitleColor(sliderData.color_subtitle ?? 'rgba(255,255,255,0.9)');
      })
      .catch((err) => {
        setError(err.message || t('contentErrorLoad'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [lang]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(SLIDER_MOBILE_QUERY);
    setIsMobile(mediaQuery.matches);
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  React.useEffect(() => {
    slides.forEach((slide) => {
      [slide.image, slide.responsive].forEach((src) => {
        if (!src || ratios[src]) {
          return;
        }

        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth && img.naturalHeight) {
            setRatios((prev) => ({ ...prev, [src]: img.naturalWidth / img.naturalHeight }));
          }
        };
        img.src = src;
      });
    });
  }, [slides]);

  React.useEffect(() => {
    if (!slides.length) {
      return undefined;
    }

    const delay = slides[current].delay ?? defaultDelay ?? 6000;
    const timer = window.setTimeout(() => {
      setCurrent((value) => (value + 1) % slides.length);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [slides, current, defaultDelay]);

  if (loading) {
    return (
      <section className="slider-wrapper">
        <div className="slider-overlay">
          <h1>{t('sliderLoading')}</h1>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="slider-wrapper">
        <div className="slider-overlay">
          <h1>{t('sliderErrorTitle')}</h1>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  const currentSlide = slides[current];
  const currentSrc = currentSlide && (isMobile && currentSlide.responsive ? currentSlide.responsive : currentSlide.image);
  const currentRatio = ratios[currentSrc];

  return (
    <section
      className="slider-wrapper"
      style={currentRatio ? { aspectRatio: currentRatio } : undefined}
    >
      {slides.map((slide, index) => (
        <article
          key={slide.title}
          className={index === current ? 'slide active' : 'slide'}
        >
          <img src={isMobile && slide.responsive ? slide.responsive : slide.image} alt={slide.title} />
          {slide.subtitle && (
            <div className="slider-overlay">
              <p style={{ color: slide.color_subtitle ?? defaultSubtitleColor }}>{slide.subtitle}</p>
            </div>
          )}
        </article>
      ))}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="slider-arrow slider-arrow-left"
            onClick={() => setCurrent((current - 1 + slides.length) % slides.length)}
            aria-label={t('sliderPrevLabel')}
          >
            ‹
          </button>
          <button
            type="button"
            className="slider-arrow slider-arrow-right"
            onClick={() => setCurrent((current + 1) % slides.length)}
            aria-label={t('sliderNextLabel')}
          >
            ›
          </button>
        </>
      )}
    </section>
  );
}
